import { supabase } from '../supabaseClient';

export interface AcordGenerateResponse {
  filename: string;
  web_url: string;
  file_id: string;
}

export interface AcordCertificate {
  file_id: string;
  name: string;
  web_url: string;
  last_modified: string;
  size: number;
}

async function authHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

export const acordService = {
  async generateCertificate(clientId: string, templateId: string): Promise<AcordGenerateResponse> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-acord-certificate`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ client_id: clientId, template_id: templateId }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to generate certificate');
    }
    return res.json();
  },

  async listCertificates(clientId: string): Promise<AcordCertificate[]> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/list-acord-certificates`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ client_id: clientId }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to list certificates');
    }
    const data = await res.json();
    return data.certificates ?? [];
  },

  async emailCertificate(params: {
    clientId: string;
    fileId: string;
    toContactIds: string[];
    subject?: string;
    bodyHtml?: string;
    additionalTemplateIds?: string[];
  }): Promise<{
    sent: true;
    recipients: Array<{ id: string; email: string; name: string }>;
    filename: string;
    attachments: string[];
  }> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-acord-email`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          client_id: params.clientId,
          file_id: params.fileId,
          to_contact_ids: params.toContactIds,
          subject: params.subject,
          body_html: params.bodyHtml,
          additional_template_ids: params.additionalTemplateIds ?? [],
        }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to send email');
    }
    return res.json();
  },

  async deleteCertificate(fileId: string): Promise<void> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-acord-certificate`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ file_id: fileId }),
      },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || 'Failed to delete certificate');
    }
  },
};
